/**
 * The chapter list on a window too narrow for the rail: a sheet that drops
 * from the header's Contents button.
 *
 * Every chapter under its part, numbered, the one being read on a soft pill,
 * with CONTENTS at its head — the way to the contents page and its search,
 * as on the rail. It closes on Esc, on a press outside it, on a chapter chosen
 * and when the window grows wide enough for the rail. Shown only under 1024
 * (`manual.css`); its motion is `motion.css`.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type React from 'react';
import { partsOf, type Doc } from '../lib/content';
import { CONTENTS } from '../lib/router';
import { Link, pad } from './DocsLink';

/** Where the rail takes over and the sheet is no longer drawn. */
const RAIL_FROM = '(min-width: 1024px)';

export const SHEET_ID = 'al-contents-sheet';

export function useContentsSheet(): { open: boolean; toggle: () => void; close: () => void } {
  const [open, setOpen] = useState(false);
  const toggle = useCallback(() => setOpen((was) => !was), []);
  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    const wide = window.matchMedia(RAIL_FROM);
    const onWide = (): void => {
      if (wide.matches) setOpen(false);
    };
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      setOpen(false);
      document.querySelector<HTMLElement>(`[aria-controls="${SHEET_ID}"]`)?.focus();
    };
    const onDown = (event: PointerEvent): void => {
      const target = event.target as Element | null;
      if (target?.closest?.(`#${SHEET_ID}, [aria-controls="${SHEET_ID}"]`)) return;
      setOpen(false);
    };
    wide.addEventListener('change', onWide);
    window.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onDown, true);
    return () => {
      wide.removeEventListener('change', onWide);
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onDown, true);
    };
  }, [open]);

  return { open, toggle, close };
}

export function ContentsSheet({
  docs,
  current,
  open,
  onClose,
  onGo
}: {
  docs: Doc[];
  current?: string;
  open: boolean;
  onClose: () => void;
  onGo: (id: string) => void;
}): React.JSX.Element {
  const sheet = useRef<HTMLDivElement>(null);

  // Opened, the sheet shows the chapter being read and takes the focus there.
  useEffect(() => {
    const box = sheet.current;
    if (!open || !box) return;
    const here = box.querySelector<HTMLElement>('[aria-current="page"]') ?? box.querySelector<HTMLElement>('a');
    if (!here) return;
    box.scrollTop = Math.max(0, here.offsetTop - box.clientHeight / 2 + here.offsetHeight / 2);
    here.focus({ preventScroll: true });
  }, [open]);

  const go = (id: string): void => {
    onClose();
    onGo(id);
  };

  return (
    <div id={SHEET_ID} ref={sheet} className="al-sheet" data-open={open ? 'true' : 'false'}>
      <nav aria-label="Contents">
        <Link id={CONTENTS} onGo={go} className="al-sheet-head">
          Contents
        </Link>
        {partsOf(docs).map((part) => (
          <div key={part.title}>
            <div className="al-sheet-part">{part.title}</div>
            <ol className="al-sheet-list">
              {part.docs.map((doc) => (
                <li key={doc.id}>
                  <Link id={doc.id} onGo={go} current={doc.id === current} className="al-sheet-link">
                    <span className="al-sheet-n">{pad(doc.order)}</span>
                    <span>{doc.title}</span>
                    {doc.beta ? <span className="al-beta">BETA</span> : null}
                  </Link>
                </li>
              ))}
            </ol>
          </div>
        ))}
      </nav>
    </div>
  );
}
